import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/SubscriptionAppCard';
import { Button } from '../components/SubscriptionAppButton';
import { Input } from '../components/SubscriptionAppInput';
import { NavBar } from '../components/NavBar';
import { MobileUnsupported } from '../components/MobileUnsupported';
import { EditTagModal } from '../components/EditTagModal';
import { useTags } from '../contexts/TagsContext';
import type { TagData } from '../contexts/TagsContext';
import { Trash2, Edit2, Plus, X, Check, Sparkles } from 'lucide-react';
interface AutomationRule {
  id: string;
  event: string;
  eventValue?: string | number;
  condition?: {
    field: string;
    operator: string;
    value: string | number;
  };
  action: 'apply' | 'remove';
}
interface TagManagementPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function TagManagementPage({
  currentPage = 'Settings',
  onNavigate
}: TagManagementPageProps) {
  const navigate = useNavigate();
  // Get tags from shared context
  const {
    allTags,
    createTag,
    updateTag,
    deleteTag
  } = useTags();
  // State
  const [isCreating, setIsCreating] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<TagData | null>(null);
  const [newTagLabel, setNewTagLabel] = useState('');
  const [newTagColor, setNewTagColor] = useState<'blue' | 'green' | 'orange' | 'purple' | 'gray' | string>('blue');
  const [newCustomColor, setNewCustomColor] = useState('#0EA5E9');
  const [editTagLabel, setEditTagLabel] = useState('');
  const [editTagColor, setEditTagColor] = useState<'blue' | 'green' | 'orange' | 'purple' | 'gray' | string>('blue');
  const [editCustomColor, setEditCustomColor] = useState('#0EA5E9');
  // Color options
  const colorOptions: Array<{
    value: 'blue' | 'green' | 'orange' | 'purple' | 'gray';
    label: string;
    preview: string;
  }> = [{
    value: 'blue',
    label: 'Blue',
    preview: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]'
  }, {
    value: 'green',
    label: 'Green',
    preview: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]'
  }, {
    value: 'orange',
    label: 'Orange',
    preview: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]'
  }, {
    value: 'purple',
    label: 'Purple',
    preview: 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]'
  }, {
    value: 'gray',
    label: 'Gray',
    preview: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'
  }];
  // Get color classes for tag display
  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]',
      green: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]',
      orange: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]',
      purple: 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]',
      gray: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'
    };
    // If it's a custom color (starts with #), return empty string
    if (color.startsWith('#')) {
      return '';
    }
    return colorMap[color as keyof typeof colorMap] || colorMap.gray;
  };
  // Get inline styles for custom colors
  const getCustomColorStyles = (color: string) => {
    if (!color.startsWith('#')) return {};
    // Calculate lighter background and border colors
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    const rgb = hexToRgb(color);
    if (!rgb) return {};
    return {
      backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
      color: color,
      borderColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`
    };
  };
  // Handlers
  const handleCreateTag = () => {
    if (!newTagLabel.trim()) return;
    const newTag: TagData = {
      id: newTagLabel.toLowerCase().replace(/\s+/g, '-'),
      label: newTagLabel,
      color: newTagColor
    };
    createTag(newTag);
    setNewTagLabel('');
    setNewTagColor('blue');
    setIsCreating(false);
  };
  const handleStartEdit = (tag: TagData) => {
    setSelectedTag(tag);
    setEditModalOpen(true);
  };
  const handleSaveEditModal = (tagData: {
    label: string;
    color: string;
    rules: AutomationRule[];
  }) => {
    if (selectedTag) {
      updateTag(selectedTag.id, {
        label: tagData.label,
        color: tagData.color,
        rules: tagData.rules
      });
    }
    setEditModalOpen(false);
    setSelectedTag(null);
  };
  const handleDeleteFromModal = () => {
    if (selectedTag && confirm('Are you sure you want to delete this tag? It will be removed from all subscriptions and contacts.')) {
      deleteTag(selectedTag.id);
      setEditModalOpen(false);
      setSelectedTag(null);
    }
  };
  const handleStartEditInline = (tag: TagData) => {
    setEditingTagId(tag.id);
    setEditTagLabel(tag.label);
    setEditTagColor(tag.color);
    // If it's a custom color, also set the custom color state
    if (tag.color.startsWith('#')) {
      setEditCustomColor(tag.color);
    }
  };
  const handleSaveEdit = () => {
    if (!editTagLabel.trim() || !editingTagId) return;
    updateTag(editingTagId, {
      label: editTagLabel,
      color: editTagColor
    });
    setEditingTagId(null);
    setEditTagLabel('');
    setEditTagColor('blue');
  };
  const handleCancelEdit = () => {
    setEditingTagId(null);
    setEditTagLabel('');
    setEditTagColor('blue');
  };
  const handleDeleteTag = (tagId: string) => {
    if (confirm('Are you sure you want to delete this tag? It will be removed from all subscriptions and contacts.')) {
      deleteTag(tagId);
    }
  };
  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewTagLabel('');
    setNewTagColor('blue');
  };
  return <>
      {/* Mobile Unsupported Message */}
      <div className="block md:hidden" data-id="element-9480">
        <MobileUnsupported data-id="element-9481" />
      </div>

      {/* Desktop Content */}
      <div className="hidden md:block bg-[#F1F5F9] min-h-full p-6" data-id="element-9482">
        {/* NavBar */}
        <div className="mb-6" data-id="element-9483">
          <NavBar tabs={[]} title="Member Tags" subtitle="Create, edit, and organize tags for your subscriptions and contacts" data-id="element-9484" />
        </div>

        {/* Main Content */}
        <div className="space-y-6" data-id="element-9485">
          <Card data-id="element-9486">
            {/* Subtitle and Create Button Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-9487">
              <p className="text-[14px] text-[#64748B]" data-id="element-9488">
                Create, edit, and organize tags for your subscriptions and
                contacts
              </p>
              {!isCreating && <Button variant="primary" onClick={() => setIsCreating(true)} iconLeft={<Plus className="w-4 h-4" data-id="element-9490" />} data-id="element-9489">
                  Create Tag
                </Button>}
            </div>

            {/* Create New Tag Form */}
            {isCreating && <div className="mb-6 p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg" data-id="element-9491">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-4" data-id="element-9492">
                  Create New Tag
                </h3>
                <div className="space-y-4" data-id="element-9493">
                  <div data-id="element-9494">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-9495">
                      Tag Name
                    </label>
                    <Input type="text" value={newTagLabel} onChange={e => setNewTagLabel(e.target.value)} placeholder="e.g., VIP Customer" autoFocus data-id="element-9496" />
                  </div>
                  <div data-id="element-9497">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-9498">
                      Color
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-id="element-9499">
                      {colorOptions.map(option => <button key={option.value} onClick={() => setNewTagColor(option.value)} className={`px-4 py-2 rounded-md border-2 transition-all ${newTagColor === option.value ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9] ring-opacity-20' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-9500">
                          <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-medium border ${option.preview}`} data-id="element-9501">
                            {option.label}
                          </span>
                        </button>)}
                    </div>

                    {/* Custom Color Picker */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-white border border-[#E2E8F0] rounded-md" data-id="element-9502">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-9503">
                        <input type="color" value={newCustomColor} onChange={e => {
                      setNewCustomColor(e.target.value);
                      setNewTagColor(e.target.value);
                    }} className="w-10 h-10 rounded border border-[#CBD5E1] cursor-pointer" data-id="element-9504" />
                        <span className="text-[12px] text-[#475569]" data-id="element-9505">
                          Custom Color
                        </span>
                      </label>
                      <div className="flex-1 w-full flex items-center gap-2" data-id="element-9506">
                        <input type="text" value={newTagColor.startsWith('#') ? newTagColor : newCustomColor} onChange={e => {
                      const value = e.target.value;
                      if (value.startsWith('#')) {
                        setNewCustomColor(value);
                        setNewTagColor(value);
                      }
                    }} placeholder="#0EA5E9" className="flex-1 px-3 py-2 text-[14px] border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20" data-id="element-9507" />
                        {newTagColor.startsWith('#') && <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium border whitespace-nowrap" style={getCustomColorStyles(newTagColor)} data-id="element-9508">
                            Preview
                          </span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-9509">
                    <Button variant="primary" onClick={handleCreateTag} disabled={!newTagLabel.trim()} iconLeft={<Check className="w-4 h-4" data-id="element-9511" />} data-id="element-9510">
                      Create Tag
                    </Button>
                    <Button variant="outline" onClick={handleCancelCreate} data-id="element-9512">
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>}

            {/* Tags List */}
            <div className="space-y-3" data-id="element-9513">
              <h3 className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-9514">
                All Tags ({allTags.length})
              </h3>

              {allTags.length === 0 ? <div className="text-center py-12" data-id="element-9515">
                  <p className="text-[14px] text-[#94A3B8]" data-id="element-9516">
                    No tags yet. Create your first tag to get started.
                  </p>
                </div> : <div className="space-y-2" data-id="element-9517">
                  {allTags.map(tag => <div key={tag.id} className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 bg-white border border-[#E2E8F0] rounded-lg hover:border-[#CBD5E1] transition-colors" data-id="element-9518">
                      {editingTagId === tag.id ?
                // Edit Mode (Inline)
                <div className="flex-1 flex flex-col lg:flex-row items-start lg:items-center gap-4" data-id="element-9519">
                          <div className="flex-1 w-full" data-id="element-9520">
                            <Input type="text" value={editTagLabel} onChange={e => setEditTagLabel(e.target.value)} placeholder="Tag name" data-id="element-9521" />
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-9522">
                            {colorOptions.map(option => <button key={option.value} onClick={() => setEditTagColor(option.value)} className={`px-3 py-1.5 rounded-md border transition-all ${editTagColor === option.value ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9] ring-opacity-20' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-9523">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${option.preview}`} data-id="element-9524">
                                  {option.label.charAt(0)}
                                </span>
                              </button>)}
                            <label className="cursor-pointer" data-id="element-9525">
                              <input type="color" value={editTagColor.startsWith('#') ? editTagColor : editCustomColor} onChange={e => {
                        setEditCustomColor(e.target.value);
                        setEditTagColor(e.target.value);
                      }} className="w-8 h-8 rounded border border-[#CBD5E1] cursor-pointer" title="Custom color" data-id="element-9526" />
                            </label>
                          </div>
                          <div className="flex gap-2" data-id="element-9527">
                            <button onClick={handleSaveEdit} disabled={!editTagLabel.trim()} className="p-2 text-[#10B981] hover:bg-[#D1FAE5] rounded-md transition-colors disabled:opacity-50" data-id="element-9528">
                              <Check className="w-5 h-5" data-id="element-9529" />
                            </button>
                            <button onClick={handleCancelEdit} className="p-2 text-[#64748B] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-9530">
                              <X className="w-5 h-5" data-id="element-9531" />
                            </button>
                          </div>
                        </div> :
                // View Mode
                <>
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1" data-id="element-9532">
                            <span className={`inline-flex items-center px-3 py-1 rounded text-[14px] font-medium border ${getColorClasses(tag.color)}`} style={tag.color.startsWith('#') ? getCustomColorStyles(tag.color) : undefined} data-id="element-9533">
                              {tag.label}
                            </span>
                            <span className="text-[12px] text-[#94A3B8]" data-id="element-9534">
                              ID: {tag.id}
                            </span>
                            {/* Automation Indicator */}
                            {tag.rules && tag.rules.length > 0 && <div className="relative group" data-id="element-9535">
                                <button onClick={() => handleStartEdit(tag)} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#F3E8FF] hover:bg-[#E9D5FF] transition-colors" title={`${tag.rules.length} automation ${tag.rules.length === 1 ? 'rule' : 'rules'}`} data-id="element-9536">
                                  <Sparkles className="w-3.5 h-3.5 text-[#8B5CF6]" data-id="element-9537" />
                                  <span className="text-[11px] font-medium text-[#7C3AED]" data-id="element-9538">
                                    {tag.rules.length}
                                  </span>
                                </button>
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-9539">
                                  {tag.rules.length} automation{' '}
                                  {tag.rules.length === 1 ? 'rule' : 'rules'}
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1" data-id="element-9540">
                                    <div className="border-4 border-transparent border-t-[#1E293B]" data-id="element-9541"></div>
                                  </div>
                                </div>
                              </div>}
                          </div>
                          <div className="flex items-center gap-2" data-id="element-9542">
                            <button onClick={() => handleStartEdit(tag)} className="p-2 text-[#0EA5E9] hover:bg-[#E0F2FE] rounded-md transition-colors" title="Edit tag" data-id="element-9543">
                              <Edit2 className="w-4 h-4" data-id="element-9544" />
                            </button>
                            <button onClick={() => handleDeleteTag(tag.id)} className="p-2 text-[#EF4444] hover:bg-[#FEE2E2] rounded-md transition-colors" title="Delete tag" data-id="element-9545">
                              <Trash2 className="w-4 h-4" data-id="element-9546" />
                            </button>
                          </div>
                        </>}
                    </div>)}
                </div>}
            </div>
          </Card>
        </div>
      </div>

      {/* Edit Tag Modal */}
      {selectedTag && <EditTagModal isOpen={editModalOpen} onClose={() => {
      setEditModalOpen(false);
      setSelectedTag(null);
    }} onSave={handleSaveEditModal} onDelete={handleDeleteFromModal} tag={selectedTag} data-id="element-9547" />}
    </>;
}